// components/admin/UserCreateDialog.jsx
import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import api from '../../services/api';
import { extractFieldErrors } from '../../utils/formErrorUtils';

export default function UserCreateDialog({ visible, onHide, onSave }) {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'employee',
    phone_number: '',
    id_number: '',
    birth_date: null,
    start_date: null,
    manager_id: null,
    approver_id: null,
    approval_hierarchy_id: null,
    expense_approval_hier_id: null,
    department_positions: [{ department_id: null, position_id: null, start_date: null, end_date: null, is_current: true }]
  });

  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [managers, setManagers] = useState([]);
  const [approvalHierarchies, setApprovalHierarchies] = useState([]);
  const [roles] = useState(['employee', 'manager', 'admin']);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      const [deptRes, posRes, mgrRes, apprRes] = await Promise.all([
        api.get('/departments'),
        api.get('/positions'),
        api.get('/users'),
        api.get('/approval-hierarchies')
      ]);
      setDepartments(deptRes.data);
      setPositions(posRes.data);
      setManagers(mgrRes.data);
      setApprovalHierarchies(apprRes.data);
    };
    fetchData();
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleDeptPosChange = (index, field, value) => {
    const updated = [...form.department_positions];
    const current = { ...updated[index] };

    if (field === 'is_current') {
      current.is_current = value;
      if (value) {
        current.end_date = null;
      }
    } else {
      current[field] = value;
    }

    if (field === 'end_date' && value === null) {
      current.end_date = null;
    }

    updated[index] = current;
    setForm(prev => ({ ...prev, department_positions: updated }));
  };

  const addDeptPos = () => {
    setForm(prev => ({
      ...prev,
      department_positions: [
        ...prev.department_positions,
        { department_id: null, position_id: null, start_date: null, end_date: null, is_current: true }
      ]
    }));
  };

  const handleSubmit = async () => {
    const allPositionsEmpty = form.department_positions.every(
      dp => !dp.department_id || !dp.position_id
    );

    const formattedDepartmentPositions = allPositionsEmpty
      ? null
      : form.department_positions.map((dp) => ({
          department_id: dp.department_id,
          position_id: dp.position_id,
          start_date: dp.start_date ? dp.start_date.toISOString().split('T')[0] : null,
          end_date: dp.is_current ? null : (dp.end_date ? dp.end_date.toISOString().split('T')[0] : null),
        }));

    const payload = {
      ...form,
      department_positions: formattedDepartmentPositions,
      birth_date: form.birth_date?.toISOString().split('T')[0],
      start_date: form.start_date?.toISOString().split('T')[0]
    };

    try {
      await api.post('/auth/register', payload);
      setErrors({});
      onSave();
      onHide();
    } catch (err) {
      if (err?.response?.status === 422) {
        const fieldErrors = {};
        extractFieldErrors(err).forEach(({ field, message }) => {
          fieldErrors[field] = message;
        });
        setErrors(fieldErrors);
      }
    }
  };


  return (
    <Dialog header="Yeni Çalışan Ekle" visible={visible} style={{ width: '700px' }} modal onHide={onHide}>
      <div className="p-fluid grid">
        <div className="col-6">
          <label>Ad</label>
          <InputText value={form.first_name} onChange={(e) => handleChange('first_name', e.target.value)} className={errors.first_name ? 'p-invalid' : ''} />
          {errors.first_name && <small className="p-error">{errors.first_name}</small>}
        </div>
        <div className="col-6">
          <label>Soyad</label>
          <InputText value={form.last_name} onChange={(e) => handleChange('last_name', e.target.value)} className={errors.last_name ? 'p-invalid' : ''} />
          {errors.last_name && <small className="p-error">{errors.last_name}</small>}
        </div>
        <div className="col-6">
          <label>Email</label>
          <InputText value={form.email} onChange={(e) => handleChange('email', e.target.value)} className={errors.email ? 'p-invalid' : ''} />
          {errors.email && <small className="p-error">{errors.email}</small>}
        </div>
        <div className="col-6">
          <label>Şifre</label>
          <InputText type="password" value={form.password} onChange={(e) => handleChange('password', e.target.value)} className={errors.password ? 'p-invalid' : ''} />
          {errors.password && <small className="p-error">{errors.password}</small>}
        </div>
        <div className="col-6">
          <label>Rol</label>
          <Dropdown value={form.role} options={roles} onChange={(e) => handleChange('role', e.value)} className={errors.role ? 'p-invalid' : ''} />
          {errors.role && <small className="p-error">{errors.role}</small>}
        </div>
        <div className="col-6">
          <label>Telefon</label>
          <InputText value={form.phone_number} onChange={(e) => handleChange('phone_number', e.target.value)} className={errors.phone_number ? 'p-invalid' : ''} />
          {errors.phone_number && <small className="p-error">{errors.phone_number}</small>}
        </div>
        <div className="col-6">
          <label>TC No</label>
          <InputText value={form.id_number} onChange={(e) => handleChange('id_number', e.target.value)} className={errors.id_number ? 'p-invalid' : ''} />
          {errors.id_number && <small className="p-error">{errors.id_number}</small>}
        </div>
        <div className="col-6">
          <label>Doğum Tarihi</label>
          <Calendar value={form.birth_date} onChange={(e) => handleChange('birth_date', e.value)} showIcon dateFormat="yy-mm-dd" className={errors.birth_date ? 'p-invalid' : ''} />
          {errors.birth_date && <small className="p-error">{errors.birth_date}</small>}
        </div>
        <div className="col-6">
          <label>İşe Başlama</label>
          <Calendar value={form.start_date} onChange={(e) => handleChange('start_date', e.value)} showIcon dateFormat="yy-mm-dd" className={errors.start_date ? 'p-invalid' : ''} />
          {errors.start_date && <small className="p-error">{errors.start_date}</small>}
        </div>
        <div className="col-6">
          <label>Yönetici</label>
          <Dropdown value={form.manager_id} options={managers.map(m => ({ label: `${m.first_name} ${m.last_name}`, value: m.id }))} onChange={(e) => handleChange('manager_id', e.value)} className={errors.manager_id ? 'p-invalid' : ''} />
          {errors.manager_id && <small className="p-error">{errors.manager_id}</small>}
        </div>
        <div className="col-6">
          <label>Onaylayan</label>
          <Dropdown value={form.approver_id} options={managers.map(m => ({ label: `${m.first_name} ${m.last_name}`, value: m.id }))} onChange={(e) => handleChange('approver_id', e.value)} className={errors.approver_id ? 'p-invalid' : ''} />
          {errors.approver_id && <small className="p-error">{errors.approver_id}</small>}
        </div>
        <div className="col-6">
          <label>İzin Onay Süreci</label>
          <Dropdown value={form.approval_hierarchy_id} options={approvalHierarchies.map(h => ({ label: h.name, value: h.id }))} onChange={(e) => handleChange('approval_hierarchy_id', e.value)} className={errors.approval_hierarchy_id ? 'p-invalid' : ''} />
          {errors.approval_hierarchy_id && <small className="p-error">{errors.approval_hierarchy_id}</small>}
        </div>
        <div className="col-6">
          <label>Harcama Onay Süreci</label>
          <Dropdown value={form.expense_approval_hier_id} options={approvalHierarchies.map(h => ({ label: h.name, value: h.id }))} onChange={(e) => handleChange('expense_approval_hier_id', e.value)} className={errors.expense_approval_hier_id ? 'p-invalid' : ''} />
          {errors.expense_approval_hier_id && <small className="p-error">{errors.expense_approval_hier_id}</small>}
        </div>

        <div className="col-12 mt-3">
          <h4>Departman & Pozisyonlar</h4>
          {form.department_positions.map((dp, index) => (
            <div className="p-fluid grid mb-2" key={index}>
              <div className="col-12 md:col-3">
                <Dropdown value={dp.department_id} options={departments.map(d => ({ label: d.name, value: d.id }))} onChange={(e) => handleDeptPosChange(index, 'department_id', e.value)} placeholder="Departman" />
              </div>
              <div className="col-12 md:col-3">
                <Dropdown value={dp.position_id} options={positions.map(p => ({ label: p.name, value: p.id }))} onChange={(e) => handleDeptPosChange(index, 'position_id', e.value)} placeholder="Pozisyon" />
              </div>
              <div className="col-12 md:col-3">
                <Calendar
                  value={dp.start_date}
                  onChange={(e) => handleDeptPosChange(index, 'start_date', e.value)}
                  showIcon
                  dateFormat="yy-mm-dd"
                  placeholder="Başlangıç Tarihi"
                />
              </div>
              <div className="col-12 md:col-3">
                <div className="flex align-items-center gap-2 mb-2">
                  <Checkbox
                    inputId={`create-dept-pos-current-${index}`}
                    checked={dp.is_current}
                    onChange={(e) => handleDeptPosChange(index, 'is_current', e.checked)}
                  />
                  <label htmlFor={`create-dept-pos-current-${index}`} className="m-0">
                    Halen
                  </label>
                </div>
                {!dp.is_current && (
                  <Calendar
                    value={dp.end_date}
                    onChange={(e) => handleDeptPosChange(index, 'end_date', e.value)}
                    showIcon
                    dateFormat="yy-mm-dd"
                    placeholder="Bitiş Tarihi"
                  />
                )}
              </div>
            </div>
          ))}
          <Button label="Yeni Ekle" icon="pi pi-plus" className="p-button-sm mt-2" onClick={addDeptPos} />
        </div>

        <div className="col-12 mt-4 flex justify-content-end">
          <Button label="Kaydet" icon="pi pi-check" onClick={handleSubmit} />
        </div>
      </div>
    </Dialog>
  );
}
